import React, { useRef, useState } from 'react';
import { Box, Button, Theme } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import Icon from '@mdi/react';
import { mdiFileImage, mdiFilePdfBox } from '@mdi/js';
import QRCode from 'react-qr-code';
import { useReactToPrint } from 'react-to-print';
import { TooltipMenu } from '../tooltip-menu';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    optionItem: {
      color: theme.alert.blue.extraLight,
      textTransform: 'none',
      paddingTop: '10px',
      justifyContent: 'flex-start',
    },
  })
);

const PNG_SIZE = 1024;
const PNG_MARGIN = 64;

interface QRCodeDownloadMenuProps {
  value: string;
  fileName: string;
  printContent: React.ReactNode;
  trigger: (onClick: (e: React.MouseEvent<HTMLElement>) => void) => React.ReactElement;
  onDone?: () => void;
}

const downloadSvgAsPng = (svg: SVGSVGElement, fileName: string) => {
  const svgData = new XMLSerializer().serializeToString(svg);
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = PNG_SIZE + PNG_MARGIN * 2;
    canvas.height = PNG_SIZE + PNG_MARGIN * 2;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, PNG_MARGIN, PNG_MARGIN, PNG_SIZE, PNG_SIZE);
    const link = document.createElement('a');
    link.download = `${fileName}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };
  img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgData)))}`;
};

export const QRCodeDownloadMenu: React.FC<QRCodeDownloadMenuProps> = ({
  value,
  fileName,
  printContent,
  trigger,
  onDone,
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);
  const pngRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    pageStyle: '@page { size: portrait; margin: 0; } @media print { body { -webkit-print-color-adjust: exact; } }',
    content: () => printRef.current,
  });

  const close = () => {
    setOpen(false);
    if (onDone) onDone();
  };

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setOpen(true);
  };

  const handlePdf = () => {
    handlePrint();
    close();
  };

  const handlePng = () => {
    const svg = pngRef.current?.querySelector('svg');
    if (svg) downloadSvgAsPng(svg, fileName);
    close();
  };

  return (
    <>
      <TooltipMenu
        open={open}
        onClose={() => setOpen(false)}
        title={
          <Box
            display='flex'
            flexDirection='column'
            alignItems='flex-start'
            style={{ padding: '0 5px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              className={classes.optionItem}
              startIcon={<Icon path={mdiFilePdfBox} size={0.7} />}
              onClick={handlePdf}
            >
              PDF
            </Button>
            <Button
              className={classes.optionItem}
              startIcon={<Icon path={mdiFileImage} size={0.7} />}
              onClick={handlePng}
            >
              PNG
            </Button>
          </Box>
        }
      >
        {trigger(handleOpen)}
      </TooltipMenu>
      <div style={{ display: 'none' }}>
        <div ref={printRef}>{printContent}</div>
        <div ref={pngRef}>
          <QRCode value={value} size={PNG_SIZE} />
        </div>
      </div>
    </>
  );
};
